import React, { useCallback, useMemo, useState } from "react";
import { Button, Empty, Input, Switch, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
	CheckOutlined,
	CloseOutlined,
	EditOutlined,
	NodeIndexOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	SorterPlanDetail,
	SorterPlanSlotWithZones,
	UpdateSorterPlanSlotRequest,
	ZoneNode,
} from "../../../types/sortingMachine";
import ZoneTreeDrawer from "./ZoneTreeDrawer";
import useLang from "../../../hooks/useLang";

type SlotPatch = Partial<
	Pick<
		UpdateSorterPlanSlotRequest,
		"displayName" | "isErrorSlot" | "isEnabled"
	>
>;

interface SlotMappingEditorProps {
	plan: SorterPlanDetail | null;
	zoneTree: ZoneNode[];
	onSlotFieldsChange: (planSlotId: number, patch: SlotPatch) => void;
	onZoneMappingSave: (planSlotId: number, zoneIds: number[]) => void;
}

const MAX_VISIBLE_TAGS = 3;

const SlotMappingEditor = ({
	plan,
	zoneTree,
	onSlotFieldsChange,
	onZoneMappingSave,
}: SlotMappingEditorProps) => {
	const { strings } = useLang("sortingMachineTab");
	const [drawerSlotId, setDrawerSlotId] = useState<number | null>(null);
	const [editingSlotId, setEditingSlotId] = useState<number | null>(null);
	const [editValue, setEditValue] = useState("");

	const slots = plan?.slots ?? [];

	const drawerSlot = useMemo(
		() => slots.find((s) => s.id === drawerSlotId) ?? null,
		[slots, drawerSlotId]
	);

	const handleStartEdit = useCallback(
		(slotId: number, currentName: string) => {
			setEditingSlotId(slotId);
			setEditValue(currentName);
		},
		[]
	);

	const handleSaveEdit = useCallback(() => {
		if (editingSlotId === null) return;
		const trimmed = editValue.trim();
		if (trimmed) {
			onSlotFieldsChange(editingSlotId, { displayName: trimmed });
		}
		setEditingSlotId(null);
	}, [editingSlotId, editValue, onSlotFieldsChange]);

	const handleCancelEdit = useCallback(() => {
		setEditingSlotId(null);
	}, []);

	const handleEditKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter") handleSaveEdit();
			if (e.key === "Escape") handleCancelEdit();
		},
		[handleSaveEdit, handleCancelEdit]
	);

	const handleEditZones = useCallback((slotId: number) => {
		setDrawerSlotId(slotId);
	}, []);

	const handleDrawerClose = useCallback(() => {
		setDrawerSlotId(null);
	}, []);

	const handleDrawerSave = useCallback(
		(slotId: number, zoneIds: number[]) => {
			onZoneMappingSave(slotId, zoneIds);
			setDrawerSlotId(null);
		},
		[onZoneMappingSave]
	);

	const columns: ColumnsType<SorterPlanSlotWithZones> = useMemo(
		() => [
			{
				title: "#",
				dataIndex: "slotNumber",
				key: "slotNumber",
				width: 50,
				render: (value: number) => (
					<span className="font-semibold text-gray-700">{value}</span>
				),
			},
			{
				title: strings?.slotMapping?.descriptionColumn ?? "Description",
				dataIndex: "displayName",
				key: "displayName",
				width: 200,
				render: (_: string, record: SorterPlanSlotWithZones) => {
					if (editingSlotId === record.id) {
						return (
							<div className="flex items-center gap-1">
								<Input
									size="small"
									value={editValue}
									onChange={(e) =>
										setEditValue(e.target.value)
									}
									onKeyDown={handleEditKeyDown}
									autoFocus
									className="w-32"
								/>
								<Button
									size="small"
									type="text"
									icon={<CheckOutlined />}
									onClick={handleSaveEdit}
								/>
								<Button
									size="small"
									type="text"
									icon={<CloseOutlined />}
									onClick={handleCancelEdit}
								/>
							</div>
						);
					}
					return (
						<span
							className="cursor-pointer hover:text-blue-500 flex items-center gap-1"
							onClick={() =>
								handleStartEdit(record.id, record.displayName)
							}
						>
							{record.displayName}
							<EditOutlined className="text-xs text-gray-400" />
						</span>
					);
				},
			},
			{
				title: strings?.slotMapping?.enabledColumn ?? "Enabled",
				dataIndex: "isEnabled",
				key: "isEnabled",
				width: 90,
				align: "center",
				render: (
					isEnabled: boolean,
					record: SorterPlanSlotWithZones
				) => (
					<Switch
						size="small"
						checked={isEnabled}
						onChange={(checked) =>
							onSlotFieldsChange(record.id, {
								isEnabled: checked,
							})
						}
					/>
				),
			},
			{
				title: strings?.slotMapping?.errorSlotToggle ?? "Error Slot",
				dataIndex: "isErrorSlot",
				key: "isErrorSlot",
				width: 100,
				align: "center",
				render: (
					isErrorSlot: boolean,
					record: SorterPlanSlotWithZones
				) => (
					<div className="flex items-center justify-center gap-2">
						<Switch
							size="small"
							checked={isErrorSlot}
							onChange={(checked) =>
								onSlotFieldsChange(record.id, {
									isErrorSlot: checked,
								})
							}
						/>
						{isErrorSlot && (
							<Tag icon={<WarningOutlined />} color="error">
								{strings?.slotMapping?.errorSlot ?? "Error"}
							</Tag>
						)}
					</div>
				),
			},
			{
				title:
					strings?.slotMapping?.assignedZonesColumn ??
					"Assigned Zones",
				key: "zones",
				render: (_: unknown, record: SorterPlanSlotWithZones) => {
					const { zones } = record;
					if (zones.length === 0) {
						return (
							<span className="text-xs text-gray-400 italic">
								{strings?.slotMapping?.noZonesAssigned ??
									"No zones assigned"}
							</span>
						);
					}
					const visible = zones.slice(0, MAX_VISIBLE_TAGS);
					const hiddenCount = zones.length - MAX_VISIBLE_TAGS;
					return (
						<div className="flex flex-wrap gap-1 items-center">
							{visible.map((zone) => (
								<Tag key={zone.zoneId} className="text-xs m-0">
									{zone.zoneDescription}
								</Tag>
							))}
							{hiddenCount > 0 && (
								<Tooltip
									title={zones
										.slice(MAX_VISIBLE_TAGS)
										.map((z) => z.zoneDescription)
										.join(", ")}
								>
									<Tag className="text-xs m-0 cursor-help">
										+{hiddenCount}{" "}
										{strings?.slotMapping?.more ?? "more"}
									</Tag>
								</Tooltip>
							)}
						</div>
					);
				},
			},
			{
				title: strings?.slotMapping?.zonesCount ?? "Zones",
				key: "zoneCount",
				width: 70,
				align: "center",
				render: (_: unknown, record: SorterPlanSlotWithZones) => (
					<span className="text-sm font-medium text-gray-600">
						{record.zones.length}
					</span>
				),
			},
			{
				title: "",
				key: "actions",
				width: 140,
				align: "center",
				render: (_: unknown, record: SorterPlanSlotWithZones) => (
					<Tooltip
						title={
							record.isEnabled
								? ""
								: strings?.slotMapping?.disabledSlotHint ??
								  "Enable the slot to edit its zones"
						}
					>
						<Button
							size="small"
							icon={<NodeIndexOutlined />}
							onClick={() => handleEditZones(record.id)}
							disabled={!record.isEnabled}
						>
							{strings?.slotMapping?.editZones ?? "Edit Zones"}
						</Button>
					</Tooltip>
				),
			},
		],
		[
			strings,
			editingSlotId,
			editValue,
			handleEditKeyDown,
			handleSaveEdit,
			handleCancelEdit,
			handleStartEdit,
			handleEditZones,
			onSlotFieldsChange,
		]
	);

	if (!plan) {
		return (
			<Empty
				description={
					strings?.slotMapping?.selectPlan ??
					"Select a plan to manage slot mappings"
				}
			/>
		);
	}

	return (
		<>
			<Table
				dataSource={slots}
				columns={columns}
				pagination={false}
				rowKey="id"
				size="middle"
				rowClassName={(record) => {
					if (record.isErrorSlot) return "bg-red-50";
					if (!record.isEnabled) return "opacity-60";
					return "";
				}}
			/>

			<ZoneTreeDrawer
				open={drawerSlotId !== null}
				slotId={drawerSlotId}
				slotDescription={
					drawerSlot
						? `#${drawerSlot.slotNumber} ${drawerSlot.displayName}`
						: ""
				}
				zoneTree={zoneTree}
				allSlots={slots}
				onSave={handleDrawerSave}
				onClose={handleDrawerClose}
			/>
		</>
	);
};

export default SlotMappingEditor;
